import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { Switch } from '../../ui/switch';
import { CampoSelect, CampoTexto } from '../../bodas/admin/comunes';
import BotonesOrden from './BotonesOrden';
import ConfigBordado from './ConfigBordado';
import EditorOpcion from './EditorOpcion';
import { claveDe, mover, nuevaOpcion, TIPOS_GRUPO } from './tiendaAdminApi';

const Interruptor = ({ label, checked, onChange, testId }) => (
  <label className="flex items-center gap-2 text-sm text-slate-700">
    <Switch checked={checked} onCheckedChange={onChange} className="min-h-0 data-[state=checked]:bg-emerald-600" data-testid={testId} />{label}
  </label>
);

const ConfigTexto = ({ grupo, set, cambiar, testId }) => (
  <div className="space-y-4">
    <div className="grid gap-3 sm:grid-cols-3">
      <CampoTexto label="Máximo de caracteres" type="number" min={1} max={60} value={grupo.max_caracteres}
        onChange={v => set('max_caracteres', Math.max(1, Math.min(60, parseInt(v, 10) || 1)))} testId={`${testId}-max`} />
      <div className="flex flex-col justify-end gap-2 sm:col-span-2">
        <Interruptor label="Solo letras (sin números ni símbolos)" checked={grupo.solo_letras} onChange={v => set('solo_letras', v)} testId={`${testId}-solo-letras`} />
        <Interruptor label="Convertir a mayúsculas" checked={grupo.mayusculas} onChange={v => set('mayusculas', v)} testId={`${testId}-mayusculas`} />
      </div>
    </div>
    <ConfigBordado grupo={grupo} cambiar={cambiar} testId={testId} />
  </div>
);

const ListaOpciones = ({ grupo, set, testId }) => {
  const cambiar = (i, o) => set('opciones', grupo.opciones.map((x, j) => (j === i ? o : x)));
  const eliminar = (i) => window.confirm('¿Eliminar esta opción? Los pedidos ya realizados la conservan.') && set('opciones', grupo.opciones.filter((_, j) => j !== i));
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {grupo.opciones.map((o, i) => (
          <EditorOpcion key={claveDe(o)} opcion={o} indice={i} total={grupo.opciones.length} testId={`${testId}-opcion-${i}`}
            onCambiar={v => cambiar(i, v)} onMover={d => set('opciones', mover(grupo.opciones, i, d))} onEliminar={() => eliminar(i)} />
        ))}
      </div>
      <button type="button" onClick={() => set('opciones', [...grupo.opciones, nuevaOpcion()])}
        className="inline-flex items-center gap-2 rounded-lg border border-dashed border-slate-400 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid={`${testId}-agregar-opcion`}>
        <Plus className="h-4 w-4" />Agregar opción
      </button>
    </div>
  );
};

export default function EditorGrupo({ grupo, indice, total, onCambiar, onMover, onEliminar, testId }) {
  const [abierto, setAbierto] = useState(true);
  const set = (campo, valor) => onCambiar({ ...grupo, [campo]: valor });
  const tipo = TIPOS_GRUPO.find(t => t.valor === grupo.tipo)?.etiqueta;
  return (
    <section className={`rounded-xl border bg-slate-50 ${grupo.activo ? 'border-slate-200' : 'border-dashed border-slate-300'}`} data-testid={testId}>
      <div className="flex items-center gap-2 p-3">
        <button type="button" onClick={() => setAbierto(!abierto)} className="flex min-w-0 flex-1 items-center gap-2 text-left" data-testid={`${testId}-toggle`}>
          {abierto ? <ChevronUp className="h-4 w-4 shrink-0 text-slate-500" /> : <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" />}
          <span className="truncate font-semibold text-slate-800">{indice + 1}. {grupo.nombre || 'Grupo sin nombre'}</span>
          <span className="hidden truncate text-xs text-slate-500 sm:inline">· {tipo} · {grupo.obligatorio ? 'Obligatorio' : 'Opcional'}{grupo.tipo !== 'texto' ? ` · ${grupo.opciones.length} opciones` : ''}{grupo.activo ? '' : ' · Inactivo'}</span>
        </button>
        <BotonesOrden indice={indice} total={total} onMover={onMover} onEliminar={onEliminar} etiqueta="grupo" testId={testId} />
      </div>
      {abierto && (
        <div className="space-y-4 border-t border-slate-200 p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <CampoTexto label="Nombre" value={grupo.nombre} onChange={v => set('nombre', v)} maxLength={60} placeholder="Ej. Cuello" testId={`${testId}-nombre`} />
            <CampoSelect label="Tipo de selección" value={grupo.tipo} onChange={v => set('tipo', v)} opciones={TIPOS_GRUPO} testId={`${testId}-tipo`} />
          </div>
          <CampoTexto label="Texto de ayuda para el cliente (opcional)" value={grupo.ayuda} onChange={v => set('ayuda', v)} maxLength={200} testId={`${testId}-ayuda`} />
          <div className="flex flex-wrap gap-5">
            <Interruptor label="Obligatorio" checked={grupo.obligatorio} onChange={v => set('obligatorio', v)} testId={`${testId}-obligatorio`} />
            <Interruptor label="Activo en la tienda" checked={grupo.activo} onChange={v => set('activo', v)} testId={`${testId}-activo`} />
          </div>
          {grupo.tipo === 'texto'
            ? <ConfigTexto grupo={grupo} set={set} cambiar={cambios => onCambiar({ ...grupo, ...cambios })} testId={testId} />
            : <ListaOpciones grupo={grupo} set={set} testId={testId} />}
        </div>
      )}
    </section>
  );
}
